<script setup lang="ts">
import type { DropdownMenuItem } from "@nuxt/ui";

const { branches, getBranchLabel, getBranchAbbr, getBranchHexColor } = useBranches();

defineProps<{
  collapsed?: boolean
}>();

// Build branch menu items from config
const branchMenuItems = computed(() => {
  // Add "All Branches" option first
  const allBranchesItem = {
    label: "Komunite (All)",
    abbreviation: "K",
    avatarUrl: `https://api.dicebear.com/7.x/initials/svg?seed=K&backgroundColor=22c55e&textColor=ffffff`
  };

  // Map config branches to menu items (handle loading state)
  const branchItems = (branches.value || []).map(branch => {
    const abbr = getBranchAbbr(branch.name);
    const hexColor = getBranchHexColor(branch.name);
    const textColorHex = getContrastTextHex(hexColor).replace('#', '');
    const avatarUrl = `https://api.dicebear.com/7.x/initials/svg?seed=${abbr}&backgroundColor=${hexColor}&textColor=${textColorHex}`;

    return {
      label: getBranchLabel(branch.name),
      abbreviation: abbr,
      hexColor: hexColor,
      avatarUrl: avatarUrl,
      branchName: branch.name
    };
  });

  return [allBranchesItem, ...branchItems];
});

const selectedBranch = ref(branchMenuItems.value[0]);

const items = computed<DropdownMenuItem[][]>(() => {
  return [branchMenuItems.value.map(branch => ({
    label: branch.label,
    avatar: {
      src: branch.avatarUrl,
      alt: branch.label
    },
    onSelect() {
      selectedBranch.value = branch;
    }
  })), [{
    label: "Manage Branches",
    icon: "i-lucide-cog",
    to: "/management/locations"
  }]];
});
</script>

<template>
  <UDropdownMenu
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{ content: collapsed ? 'w-40' : 'w-(--reka-dropdown-menu-trigger-width)' }"
  >
    <template #item="{ item }">
      <div v-if="item.avatar" class="flex items-center gap-2 w-full">
        <UAvatar
          :src="item.avatar?.src"
          :alt="item.avatar?.alt"
          size="xs"
        />
        <span>{{ item.label }}</span>
      </div>
      <div v-else-if="item.icon" class="flex items-center gap-2 w-full">
        <UIcon :name="item.icon" class="w-4 h-4" />
        <span>{{ item.label }}</span>
      </div>
    </template>

    <UButton
      :label="collapsed ? undefined : selectedBranch?.label"
      :trailing-icon="collapsed ? undefined : 'i-lucide-chevrons-up-down'"
      color="neutral"
      variant="ghost"
      block
      :square="collapsed"
      class="data-[state=open]:bg-elevated"
      :class="[!collapsed && 'py-2']"
      :ui="{
        trailingIcon: 'text-dimmed'
      }"
    >
      <template #leading>
        <UAvatar
          :src="selectedBranch?.avatarUrl"
          :alt="selectedBranch?.label"
          size="xs"
        />
      </template>
    </UButton>
  </UDropdownMenu>
</template>
